/**
 * アプリ内通知のデータフック（ヘッダー通知ベル用）
 *
 * 未読数はポーリングで取得する。API コストを抑えるため、
 * - ポーリングのタイマーはタブがアクティブなときだけ動かす
 * - ページアクセスからの経過時間に応じて間隔を広げる（pollIntervalMs 参照）
 * - タブに戻ってきたときは、直近の取得から間もなければ取り直さない
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { notificationQueryKeys } from '@/queryKeys/notifications';
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
  type AppNotificationListResponse,
} from '@/services/appNotifications';
import {
  NOTIF_POLL_BASE_MS,
  NOTIF_POLL_BASE_PHASE_MS,
  NOTIF_POLL_MAX_MS,
} from '@/constants/notificationTypes';

/** タブがアクティブか（TanStack Query の focusManager と同じ visibilitychange 基準） */
function subscribeToVisibility(callback: () => void): () => void {
  document.addEventListener('visibilitychange', callback);
  return () => document.removeEventListener('visibilitychange', callback);
}

function useIsTabVisible(): boolean {
  return useSyncExternalStore(
    subscribeToVisibility,
    () => document.visibilityState === 'visible',
    () => true
  );
}

/**
 * ページを開いてからの経過時間に対するポーリング間隔
 *
 * 最初の NOTIF_POLL_BASE_PHASE_MS は基本間隔のまま、それ以降は経過時間が倍になる
 * たびに間隔も倍にして NOTIF_POLL_MAX_MS で頭打ちにする。
 * 副作用を持たない純粋関数にしておくこと（refetchInterval は fetch 後だけでなく
 * オプション更新＝再レンダーのたびに評価されるため、ref を書き換えると回数がずれる）。
 */
function pollIntervalMs(elapsedMs: number): number {
  if (elapsedMs <= NOTIF_POLL_BASE_PHASE_MS) return NOTIF_POLL_BASE_MS;
  const steps = Math.floor(Math.log2(elapsedMs / NOTIF_POLL_BASE_PHASE_MS)) + 1;
  return Math.min(NOTIF_POLL_BASE_MS * 2 ** steps, NOTIF_POLL_MAX_MS);
}

export function useUnreadNotificationCount() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const isTabVisible = useIsTabVisible();
  // レンダー中に Date.now() を呼ばないよう遅延初期化（react-hooks/purity 対応）
  const [openedAt] = useState(() => Date.now());
  const userId = user?.id ?? 'anonymous';

  const query = useQuery({
    queryKey: notificationQueryKeys.unreadCount(userId),
    enabled: isAuthenticated,
    staleTime: NOTIF_POLL_BASE_MS,
    queryFn: getUnreadCount,
    refetchInterval: (q) => {
      // 認証エラー等で失敗が続く場合はポーリングを止める
      if (q.state.status === 'error') return false;
      // 非アクティブなタブではタイマー自体を止める（アクティブ復帰時に再開）
      if (!isTabVisible) return false;
      return pollIntervalMs(Date.now() - openedAt);
    },
  });

  const { refetch } = query;
  const wasTabVisibleRef = useRef(true);

  // 非アクティブ→アクティブに戻ったときだけ取り直す。
  // 直近の取得から基本間隔も経っていなければ何もしない（タブを行き来しても増やさない）
  useEffect(() => {
    const wasVisible = wasTabVisibleRef.current;
    wasTabVisibleRef.current = isTabVisible;
    if (!isAuthenticated || !isTabVisible || wasVisible) return;
    const lastFetchedAt =
      queryClient.getQueryState(notificationQueryKeys.unreadCount(userId))
        ?.dataUpdatedAt ?? 0;
    if (Date.now() - lastFetchedAt < NOTIF_POLL_BASE_MS) return;
    void refetch();
  }, [isAuthenticated, isTabVisible, queryClient, refetch, userId]);

  return {
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
  };
}

export function useNotificationList(open: boolean) {
  const { isAuthenticated, user } = useAuth();

  const query = useInfiniteQuery({
    queryKey: notificationQueryKeys.list(user?.id ?? 'anonymous'),
    enabled: open && isAuthenticated,
    staleTime: 0,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => getNotifications(pageParam),
    getNextPageParam: (lastPage: AppNotificationListResponse) =>
      lastPage.nextCursor ?? undefined,
  });

  const notifications: AppNotification[] =
    query.data?.pages.flatMap((page) => page.notifications ?? []) ?? [];

  return {
    notifications,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

/** 既読化ミューテーション（リストは楽観更新、未読数は invalidate） */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? 'anonymous';

  const applyReadToCache = useCallback(
    (notificationId: string | null) => {
      queryClient.setQueryData(
        notificationQueryKeys.list(userId),
        (data: { pages: AppNotificationListResponse[] } | undefined) => {
          if (!data) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              notifications: (page.notifications ?? []).map((n) =>
                notificationId === null || n.notificationId === notificationId
                  ? { ...n, read: true }
                  : n
              ),
            })),
          };
        }
      );
    },
    [queryClient, userId]
  );

  const invalidateUnreadCount = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: notificationQueryKeys.unreadCount(userId),
    });
  }, [queryClient, userId]);

  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onMutate: (notificationId: string) => applyReadToCache(notificationId),
    onSettled: invalidateUnreadCount,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => applyReadToCache(null),
    onSettled: invalidateUnreadCount,
  });

  return { markOne, markAll };
}
